import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import {
  addControllerToCompany,
  addControllerToUser,
  addUserToCompany,
  createApiKey,
  deleteApiKey,
  deleteCompany,
  fetchApiKeys,
  fetchCompanyDetail,
  removeControllerFromCompany,
  removeControllerFromUser,
  removeUserFromCompany,
  revokeApiKey,
  rotateApiKey,
  updateCompany,
} from '@/features/companies/companyDetailApi'
import type { SiteAccessMode, SubscriptionTier } from '@/features/companies/companyDetailTypes'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'

type SelectOption = { value: string; label: string }

/**
 * Text input + a live filtered options list rendered below it (no
 * dropdown/combobox component exists in this codebase, and a plain <select>
 * can't do this - typing to filter would move focus off the select and
 * close its native popup before the filtered list is visible). The list
 * stays open while the input is focused; picking an option or clearing the
 * input closes it and updates the selection.
 */
function FilterableSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder: string
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const selected = options.find((o) => o.value === value)
  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))

  return (
    <div className="relative">
      <input
        value={isOpen ? query : (selected?.label ?? '')}
        onChange={(e) => {
          setQuery(e.target.value)
          if (value) onChange('')
        }}
        onFocus={() => {
          setQuery('')
          setIsOpen(true)
        }}
        onBlur={() => setIsOpen(false)}
        placeholder={placeholder}
        disabled={disabled || options.length === 0}
        className="w-full rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary disabled:opacity-50"
      />
      {isOpen && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full min-w-[12rem] overflow-auto rounded border border-border bg-card text-sm shadow-lg">
          {filtered.length === 0 && <li className="px-2 py-1.5 text-text-secondary">No matches</li>}
          {filtered.map((o) => (
            <li
              key={o.value}
              onMouseDown={(e) => {
                e.preventDefault()
                onChange(o.value)
                setQuery('')
                setIsOpen(false)
              }}
              className="cursor-pointer px-2 py-1.5 text-text-primary hover:bg-bg-secondary"
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Ports includes/company_details.html in full: read-only info, users list
 * (add/remove), controllers list (add/remove), API-key management, and
 * delete-company — all gated by the same clients.edit/clients.delete/
 * clients.api_keys.manage permissions the legacy routes already enforced.
 */
export default function CompanyDetailPage() {
  const { clientId = '' } = useParams<{ clientId: string }>()
  const { hasPermission } = useAuth()
  const canManageKeys = hasPermission('clients.api_keys.manage')
  const canEdit = hasPermission('clients.edit')
  const canDelete = hasPermission('clients.delete')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const companyQuery = useQuery({
    queryKey: ['company', clientId],
    queryFn: () => fetchCompanyDetail(clientId),
    enabled: Boolean(clientId),
  })

  // New capability, not a legacy restoration - legacy never let you rename
  // a company or change its capacity after creation, only create/delete.
  const [isEditingCompany, setIsEditingCompany] = useState(false)
  const [editName, setEditName] = useState('')
  const [editCapacity, setEditCapacity] = useState('')
  const updateCompanyMutation = useMutation({
    mutationFn: () => updateCompany(clientId, { company_name: editName, capacity: editCapacity }),
    onSuccess: () => {
      setIsEditingCompany(false)
      queryClient.invalidateQueries({ queryKey: ['company', clientId] })
      queryClient.invalidateQueries({ queryKey: ['companies'] })
    },
  })

  const invalidateCompany = () => queryClient.invalidateQueries({ queryKey: ['company', clientId] })

  const [selectedUserId, setSelectedUserId] = useState('')
  const addUserMutation = useMutation({
    mutationFn: () => addUserToCompany(clientId, selectedUserId),
    onSuccess: () => {
      setSelectedUserId('')
      invalidateCompany()
    },
  })
  const removeUserMutation = useMutation({
    mutationFn: (userId: string) => removeUserFromCompany(clientId, userId),
    onSuccess: invalidateCompany,
  })

  // Per-user controller access (user_controllers) - which of the company's
  // controllers this specific user is scoped to, distinct from the
  // company's own controller list above. `expandedUserId` gates showing
  // one user's panel at a time; `selectedControllerByUser` remembers each
  // user's in-progress "add" pick independently so switching between
  // panels doesn't clobber a half-made selection.
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null)
  const [selectedControllerByUser, setSelectedControllerByUser] = useState<Record<string, string>>({})
  const addUserControllerMutation = useMutation({
    mutationFn: ({ userId, controllerId }: { userId: string; controllerId: string }) =>
      addControllerToUser(userId, controllerId),
    onSuccess: (_data, { userId }) => {
      setSelectedControllerByUser((prev) => ({ ...prev, [userId]: '' }))
      invalidateCompany()
    },
  })
  const removeUserControllerMutation = useMutation({
    mutationFn: ({ userId, controllerId }: { userId: string; controllerId: string }) =>
      removeControllerFromUser(userId, controllerId),
    onSuccess: invalidateCompany,
  })

  const [selectedControllerId, setSelectedControllerId] = useState('')
  const addControllerMutation = useMutation({
    mutationFn: () => addControllerToCompany(clientId, selectedControllerId),
    onSuccess: () => {
      setSelectedControllerId('')
      invalidateCompany()
    },
  })
  const removeControllerMutation = useMutation({
    mutationFn: (controllerId: string) => removeControllerFromCompany(clientId, controllerId),
    onSuccess: invalidateCompany,
  })

  const deleteCompanyMutation = useMutation({
    mutationFn: () => deleteCompany(clientId),
    onSuccess: () => navigate('/companies', { replace: true }),
  })

  const keysQuery = useQuery({
    queryKey: ['company', clientId, 'api-keys'],
    queryFn: () => fetchApiKeys(clientId),
    enabled: Boolean(clientId) && canManageKeys,
  })

  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyTier, setNewKeyTier] = useState<SubscriptionTier>('standard')
  const [newKeyReveal, setNewKeyReveal] = useState<string | null>(null)

  const invalidateKeys = () => queryClient.invalidateQueries({ queryKey: ['company', clientId, 'api-keys'] })

  const createMutation = useMutation({
    mutationFn: () =>
      createApiKey(clientId, {
        name: newKeyName.trim(),
        subscription_tier: newKeyTier,
        site_access_mode: 'all' as SiteAccessMode,
        controller_ids: [],
      }),
    onSuccess: (result) => {
      setNewKeyReveal(result.raw_key)
      setNewKeyName('')
      invalidateKeys()
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(clientId, keyId),
    onSuccess: invalidateKeys,
  })

  const rotateMutation = useMutation({
    mutationFn: (keyId: string) => rotateApiKey(clientId, keyId, 7),
    onSuccess: (result) => {
      setNewKeyReveal(result.raw_key)
      invalidateKeys()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => deleteApiKey(clientId, keyId),
    onSuccess: invalidateKeys,
  })

  if (companyQuery.isLoading) return <LoadingState label="Loading company…" />
  if (companyQuery.isError) {
    return (
      <ErrorState
        message={companyQuery.error instanceof Error ? companyQuery.error.message : 'Failed to load company.'}
        onRetry={() => companyQuery.refetch()}
      />
    )
  }
  const company = companyQuery.data
  if (!company) return null

  return (
    <div className="space-y-6">
      <div>
        <Link to="/companies" className="text-sm text-accent hover:underline">
          ← Back to Companies
        </Link>

        {isEditingCompany ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (editName.trim()) updateCompanyMutation.mutate()
            }}
            className="mt-2 flex flex-wrap items-end gap-2"
          >
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Company name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
                className="rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Capacity (kW)</label>
              <input
                type="number"
                step="any"
                value={editCapacity}
                onChange={(e) => setEditCapacity(e.target.value)}
                className="rounded border border-border bg-input px-2 py-1.5 text-sm text-text-primary"
              />
            </div>
            <button
              type="submit"
              disabled={!editName.trim() || updateCompanyMutation.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {updateCompanyMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setIsEditingCompany(false)}
              className="rounded border border-border px-3 py-1.5 text-sm text-text-primary hover:bg-bg-secondary"
            >
              Cancel
            </button>
            {updateCompanyMutation.isError && <p className="w-full text-xs text-error">Failed to save changes.</p>}
          </form>
        ) : (
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <div>
              <h1 className="text-xl font-semibold text-text-primary">{company.company_name}</h1>
              {company.capacity && <p className="text-sm text-text-secondary">Capacity: {company.capacity}</p>}
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => {
                  setEditName(company.company_name)
                  setEditCapacity(company.capacity ?? '')
                  setIsEditingCompany(true)
                }}
                className="text-xs text-accent hover:underline"
              >
                Edit
              </button>
            )}
          </div>
        )}
      </div>

      <section className="rounded border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">
          Users ({company.users.length})
        </h2>
        {company.users.length === 0 ? (
          <EmptyState message="No users assigned to this company." />
        ) : (
          <ul className="divide-y divide-border text-sm">
            {company.users.map((u) => {
              const isExpanded = expandedUserId === u.user_id
              const assignedControllerIds = new Set(u.controllers.map((c) => c.controller_id))
              const availableForUser = company.controllers.filter((c) => !assignedControllerIds.has(c.controller_id))
              const selectedControllerForUser = selectedControllerByUser[u.user_id] ?? ''
              return (
                <li key={u.user_id} className="py-2 text-text-primary">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setExpandedUserId(isExpanded ? null : u.user_id)}
                      className="flex items-center gap-1 text-left hover:underline"
                    >
                      <span className={`inline-block text-text-secondary transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                      {u.firstname} {u.lastname} <span className="text-text-secondary">({u.email})</span>
                      <span className="text-xs text-text-secondary">
                        · {u.controllers.length} controller{u.controllers.length === 1 ? '' : 's'}
                      </span>
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Remove ${u.email} from this company?`)) removeUserMutation.mutate(u.user_id)
                        }}
                        className="text-xs text-error hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="ml-5 mt-2 rounded border border-border bg-bg-secondary p-3">
                      <p className="mb-2 text-xs font-semibold text-text-secondary">
                        Controllers this user can access
                      </p>
                      {u.controllers.length === 0 ? (
                        <p className="mb-2 text-xs text-text-secondary">
                          No controllers assigned - this user won't see any sites on their dashboard.
                        </p>
                      ) : (
                        <ul className="mb-2 divide-y divide-border text-sm">
                          {u.controllers.map((c) => (
                            <li key={c.controller_id} className="flex items-center justify-between py-1.5">
                              <span>{c.controller_name || c.controller_id}</span>
                              {canEdit && (
                                <button
                                  type="button"
                                  onClick={() => removeUserControllerMutation.mutate({ userId: u.user_id, controllerId: c.controller_id })}
                                  className="text-xs text-error hover:underline"
                                >
                                  Remove
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {canEdit && (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault()
                            if (selectedControllerForUser) {
                              addUserControllerMutation.mutate({ userId: u.user_id, controllerId: selectedControllerForUser })
                            }
                          }}
                          className="flex items-end gap-2"
                        >
                          <div>
                            <label className="mb-1 block text-xs text-text-secondary">Add controller for this user</label>
                            <FilterableSelect
                              value={selectedControllerForUser}
                              onChange={(next) => setSelectedControllerByUser((prev) => ({ ...prev, [u.user_id]: next }))}
                              options={availableForUser.map((c) => ({
                                value: c.controller_id,
                                label: c.controller_name || c.controller_id,
                              }))}
                              placeholder={
                                availableForUser.length === 0 ? 'All company controllers already assigned' : 'Select a controller…'
                              }
                              disabled={availableForUser.length === 0}
                            />
                          </div>
                          <button
                            type="submit"
                            disabled={!selectedControllerForUser || addUserControllerMutation.isPending}
                            className="rounded bg-brand px-3 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                          >
                            Add
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
        {canEdit && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (selectedUserId) addUserMutation.mutate()
            }}
            className="mt-3 flex items-end gap-2"
          >
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Add user</label>
              <FilterableSelect
                value={selectedUserId}
                onChange={setSelectedUserId}
                options={company.available_users.map((u) => ({
                  value: u.user_id,
                  label: `${u.firstname} ${u.lastname} (${u.email})`,
                }))}
                placeholder="Select a user…"
              />
            </div>
            <button
              type="submit"
              disabled={!selectedUserId || addUserMutation.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Add
            </button>
          </form>
        )}
      </section>

      <section className="rounded border border-border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-text-primary">
          Controllers ({company.controllers.length})
        </h2>
        {company.controllers.length === 0 ? (
          <EmptyState message="No controllers assigned to this company." />
        ) : (
          <ul className="divide-y divide-border text-sm">
            {company.controllers.map((c) => (
              <li key={c.controller_id} className="flex items-center justify-between py-2 text-text-primary">
                <span>
                  {c.controller_name || c.controller_id}{' '}
                  <span className="text-text-secondary">
                    {c.app_type && `· ${c.app_type}`} {c.capacity && `· ${c.capacity}`}
                  </span>
                </span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Remove ${c.controller_name || c.controller_id} from this company?`))
                        removeControllerMutation.mutate(c.controller_id)
                    }}
                    className="text-xs text-error hover:underline"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (selectedControllerId) addControllerMutation.mutate()
            }}
            className="mt-3 flex items-end gap-2"
          >
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Add controller</label>
              <FilterableSelect
                value={selectedControllerId}
                onChange={setSelectedControllerId}
                options={company.available_controllers.map((c) => ({
                  value: c.controller_id,
                  label: c.controller_name || c.controller_id,
                }))}
                placeholder="Select a controller…"
              />
            </div>
            <button
              type="submit"
              disabled={!selectedControllerId || addControllerMutation.isPending}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              Add
            </button>
            {/* Legacy's company_details.html had this exact "Create New"
                link next to the add-controller dropdown for a controller
                that doesn't exist yet - restored here, pre-selecting this
                company on the create form so the new controller already
                belongs to it once saved. */}
            <Link
              to={`/controllers/new?company_id=${clientId}`}
              className="rounded border border-border px-3 py-1.5 text-sm text-brand hover:bg-bg-secondary"
            >
              + Create New
            </Link>
          </form>
        )}
      </section>

      {canManageKeys && (
        <section className="rounded border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-text-primary">API Keys</h2>

          {newKeyReveal && (
            <div className="mb-4 rounded border border-brand bg-bg-secondary p-3 text-sm">
              <p className="mb-1 font-medium text-brand">New key (copy it now — it won't be shown again):</p>
              <code className="break-all">{newKeyReveal}</code>
              <button
                type="button"
                onClick={() => setNewKeyReveal(null)}
                className="ml-3 text-xs text-text-secondary hover:underline"
              >
                Dismiss
              </button>
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (newKeyName.trim()) createMutation.mutate()
            }}
            className="mb-4 flex flex-wrap items-end gap-2"
          >
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Key name</label>
              <input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="rounded border border-border bg-input px-2 py-1 text-sm text-text-primary"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-text-secondary">Tier</label>
              <select
                value={newKeyTier}
                onChange={(e) => setNewKeyTier(e.target.value as SubscriptionTier)}
                className="rounded border border-border bg-input px-2 py-1 text-sm text-text-primary"
              >
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending || !newKeyName.trim()}
              className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Generating…' : 'Generate Key'}
            </button>
          </form>

          {keysQuery.isLoading && <LoadingState label="Loading API keys…" />}
          {keysQuery.data && keysQuery.data.length === 0 && <EmptyState message="No API keys yet." />}
          {keysQuery.data && keysQuery.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-text-primary">
                    <th className="py-1.5 pr-3 font-semibold">Name</th>
                    <th className="py-1.5 pr-3 font-semibold">Key</th>
                    <th className="py-1.5 pr-3 font-semibold">Tier</th>
                    <th className="py-1.5 pr-3 font-semibold">Status</th>
                    <th className="py-1.5 pr-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keysQuery.data.map((key) => (
                    <tr key={key.id} className="border-b border-border last:border-0">
                      <td className="py-1.5 pr-3 text-text-primary">{key.name}</td>
                      <td className="py-1.5 pr-3 font-mono text-xs text-text-secondary">{key.key_prefix}…</td>
                      <td className="py-1.5 pr-3 text-text-primary">{key.subscription_tier}</td>
                      <td className="py-1.5 pr-3 text-text-primary">{key.lifecycle_status}</td>
                      <td className="py-1.5 pr-3">
                        {key.is_active && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => rotateMutation.mutate(key.id)}
                              className="text-xs text-accent hover:underline"
                            >
                              Rotate
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(`Revoke key "${key.name}"?`)) revokeMutation.mutate(key.id)
                              }}
                              className="text-xs text-error hover:underline"
                            >
                              Revoke
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                if (confirm(`Delete key "${key.name}"?`)) deleteMutation.mutate(key.id)
                              }}
                              className="text-xs text-error hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {canDelete && (
        <section className="rounded border border-error p-4">
          <h2 className="mb-2 text-sm font-semibold text-error">Danger zone</h2>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete company "${company.company_name}"? This cannot be undone.`)) {
                deleteCompanyMutation.mutate()
              }
            }}
            disabled={deleteCompanyMutation.isPending}
            className="rounded border border-error px-3 py-1.5 text-sm font-medium text-error hover:bg-error hover:text-white disabled:opacity-50"
          >
            {deleteCompanyMutation.isPending ? 'Deleting…' : 'Delete Company'}
          </button>
        </section>
      )}
    </div>
  )
}
